import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { WhiteSpaceValidator } from '@shure/shared/angular/ui/validators/whitespace';

export enum CommonNameValidationRules {
	// dis-allowed: :/\<>"|?*^,#&~{} and not allow start with .
	Regex = '^(?!\\.)^((?!:|\\/|\\\\|<|>|\\"|\\||\\?|\\*|\\^|,|#|&|~|{|}).)*$',
	AsciiRange = '[\x20-\xFF]+'
}

export enum DeviceNameValidationRules {
	Regex = '^(?!-)[A-Za-z0-9-]{1,31}(?<!-)$'
}

export enum RoomAndIpGroupNameValidationRules {
	MaxLength = 32
}

export enum RoomPasswordValidationRules {
	MinLength = 8,
	MaxLength = 64
}

export enum RoomDescriptionValidationRules {
	MaxLength = 125
}

export enum PresetNameValidationRules {
	MaxLength = 25
}

export function getDeviceNameValidators(maxLength: number, nameConstraint?: string): ValidatorFn[] {
	return [
		Validators.required,
		Validators.maxLength(maxLength),
		Validators.pattern(nameConstraint || DeviceNameValidationRules.Regex)
	];
}

export function getRoomPasswordValidators(): ValidatorFn[] {
	return [
		Validators.required,
		Validators.minLength(RoomPasswordValidationRules.MinLength),
		Validators.maxLength(RoomPasswordValidationRules.MaxLength)
	];
}

export function getRoomDescriptionValidators(): ValidatorFn[] {
	return [Validators.maxLength(RoomDescriptionValidationRules.MaxLength)];
}

export function getNumberValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (!value) {
			return null;
		}

		if (isNaN(value) || value.toString().includes(' ')) {
			return <ValidationErrors>{ numberinvalid: true };
		}

		return null;
	};
}

export function numberInRangeValidator(range: { min: number; max: number }): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (value === null || (value >= range.min && value <= range.max)) {
			return null;
		}

		return <ValidationErrors>{ outsideRange: true };
	};
}

export function defaultPresetNameValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value.trim();
		const regexPattern = /^Preset ([1-9]|10) \(EMPTY\)$/;
		return regexPattern.test(value) ? { defaultPresetName: true } : null;
	};
}

/**
 * Getting validators for preset name.
 * - Validators.required
 * - WhiteSpaceValidator
 * - Validators.maxLength
 * - Validators.pattern
 */
export function getPresetNameValidators(): ValidatorFn[] {
	return [
		Validators.required,
		WhiteSpaceValidator.valueHasOnlyWhiteSpaces,
		Validators.maxLength(PresetNameValidationRules.MaxLength),
		Validators.pattern(CommonNameValidationRules.Regex),
		Validators.pattern(CommonNameValidationRules.AsciiRange)
	];
}

/**
 * Getting validators for room name and ip group name.
 * - Validators.required
 * - WhiteSpaceValidator
 * - Validators.maxLength
 * - Validators.pattern
 */
export function getRoomAndIpGroupNameValidators(): ValidatorFn[] {
	return [
		Validators.required,
		WhiteSpaceValidator.valueHasOnlyWhiteSpaces,
		Validators.maxLength(RoomAndIpGroupNameValidationRules.MaxLength),
		Validators.pattern(CommonNameValidationRules.Regex)
	];
}

export function valueContainsInvalidCharacter(control: FormControl): ValidationErrors | null {
	if (!control.value) {
		return null;
	}

	const forbiddenCharactersRegEx = /[:./\\<>"|?*^,#&~]/;
	const stringValue = <string>control.value;
	const index = stringValue.search(forbiddenCharactersRegEx);
	return index >= 0
		? {
				invalidCharacter: {
					firstViolation: stringValue[index]
				}
		  }
		: null;
}

// exporting the regex so it can be used outside the context of a Validator
export const macAddressRegEx = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
export function getMacAddresValidators(): ValidatorFn[] {
	return [Validators.required, Validators.pattern(macAddressRegEx)];
}

// exporting the regex so it can be used outside the context of a Validator
// SerialNumber... one of three formats accepted:
// 1 - Alphanumeric + underscores, at least 1 character
// 2 - UUID v5 format (8-4-4-4-12). Added for IMXR
// 3 - 9-19 format for Husk Devices
// Note: this regex matches the format used on the CDM BE.
export const serialNumberRegEx =
	/^([a-z0-9_]+)$|^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$|^([a-z0-9]{9}-[0-9a-z]{19})$/i;
export function getSerialNumberValidators(): ValidatorFn[] {
	return [Validators.required, Validators.pattern(serialNumberRegEx)];
}
